import React, { useMemo } from 'react';
import { getIntl } from '../../../utils/intl';
import { Field } from 'formik';
import {
    ContactDetailField,
    nameOrCompanyRequiredCustomValidators,
    useGetRenderContactDetailsFormFields,
} from '../ContactDetailsForm';
import { FormDirtyStateTracker } from '../FormExitValidation';
import { handleFormikSubmit } from '../../../utils/handleFormikSubmit';
import { CenteredFormRow, Form, SplitFormRow } from '../../v2BaseComponents/Form';
import Stack, { FormStack } from '../../v2BaseComponents/Stack';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import { APITypes } from '../../../primary-record-types';
import { required } from '../../../utils/validators';
import urls from '../../../utils/urls';
import { AVATAR_UPLOAD_QUALITY } from '../../../utils/constants';
import {
    TempAndPersistedAvatarState,
    avatarStateToAvatarChange,
} from '../../v2BaseComponents/ImagePicker/TempImagePicker';
import { useMaybeNotLoadedIsOrganizationOwner } from '../../../hooks/api/useOrganization';
import Text from '../../v2BaseComponents/Text';
import { getSpacing } from '../../../utils/spacing';
import { useEnvironment } from '../../../hooks/useEnvironment';
import MaybeStackedImageField from '../MaybeStackedImageField';

const intl = getIntl('OrganizationDetailsForm', {
    submit: 'Submit',
    name: 'Organization Name',
    description: 'Organization Description',
    organizationLogo: 'Organization Logo',
    organizationAvatar: 'Organization Avatar',
    addLogo: 'Add Logo',
    addAvatar: 'Add Avatar',
    isPublic: 'Publicly Visible',
    publicExplainer:
        'Public visibility is required to show the organization name, description, and logo during the invitation process.',
    organizationLogoMainHelpText: 'Used in the collaborator details tab, emails to clients, and in billing screens.',
    organizationLogoSubHelpText: 'Suggested: JPEG or PNG at least 128px wide and 128px tall',
    organizationAvatarMainHelpText: 'Used as the collaborator image in conversations',
    organizationAvatarSubHelpText: 'Suggested: square JPEG or PNG at least 128px wide and at least 128px tall',
    brandingPrimaryColorTitle: 'Brand Color',
    brandingPrimaryColorSubTitle: 'Used for background color',
    brandingSecondaryColorTitle: 'Accent Color',
    brandingSecondaryColorSubTitle: 'Used for buttons color',
    logoDimensionError: 'Must be at least 128px wide and 128px tall',
    avatarDimensionError: 'Avatar must be square and at least 128px wide and 128px tall',
});

export type FormValue = APITypes.organizationService.CreateOrganizationRequest;

type InternalFormValue = Omit<FormValue, 'avatar' | 'logo'> & {
    avatar: TempAndPersistedAvatarState | null;
    logo: TempAndPersistedAvatarState | null;
};

export type OrganizationDetailsFormOwnProps = {
    onSubmit: (values: FormValue) => void;
    isDisabled?: boolean;
    initialValues: FormValue;
    /**
     * Whether or not this is a new organization that is being created.
     */
    isNewOrganization?: boolean;
    organizationId?: string;
    onCancel?: () => void;
};

const contactDetailsFields: ContactDetailField[] = [
    'address1',
    'address2',
    'city',
    'state',
    'zipCode',
    'email',
    'phoneNumber',
    'faxNumber',
    'url',
];

export type OrganizationDetailsFormProps = OrganizationDetailsFormOwnProps;

const OrganizationDetailsForm: React.FC<OrganizationDetailsFormProps> = ({
    onSubmit,
    isDisabled,
    isNewOrganization,
    initialValues,
    organizationId,
    onCancel,
}) => {
    const isDev = useEnvironment() === 'dev';
    const { renderFields } = useGetRenderContactDetailsFormFields({
        isDisabled: isDisabled,
        fields: contactDetailsFields,
        customValidators: nameOrCompanyRequiredCustomValidators,
    });
    const isOwner = useMaybeNotLoadedIsOrganizationOwner();

    const internalFormValue = useMemo<InternalFormValue>(() => {
        return {
            ...initialValues,
            avatar: null,
            logo: null,
        };
    }, [initialValues]);

    const renderColorPicker = (fieldName: string, title: string, secondary: string) => {
        if (!isDev) return null;

        return (
            <CenteredFormRow>
                <Stack horizontal fullWidth justify="flex-start" align="center">
                    <Stack vertical style={{ width: 220 }} space={3}>
                        <Stack horizontal fullWidth justify="space-between" align="center">
                            <Text variant="bold-body-1">{title}</Text>
                            <Field name={fieldName} component={FormikInputs.ColorPickerInput} />
                        </Stack>
                        <Text variant="caption-2" color="Gray-Text">
                            {secondary}
                        </Text>
                    </Stack>
                </Stack>
            </CenteredFormRow>
        );
    };

    return (
        <Form
            actionContainerSubmit
            initialValues={internalFormValue}
            onSubmit={(values, helpers) => {
                onSubmit({
                    ...values,
                    avatar: avatarStateToAvatarChange(values.avatar),
                    logo: avatarStateToAvatarChange(values.logo),
                });
                handleFormikSubmit(values, helpers);
            }}
            onCancel={onCancel}
        >
            {() => {
                return (
                    <FormStack>
                        <SplitFormRow>
                            <Field
                                name="name"
                                component={FormikInputs.Input}
                                validate={required}
                                isRequired
                                title={intl('name')}
                            />
                        </SplitFormRow>
                        <MaybeStackedImageField
                            helpTextMain={intl('organizationLogoMainHelpText')}
                            subHelpText={intl('organizationLogoSubHelpText')}
                        >
                            <Field
                                name="logo"
                                component={FormikInputs.TempAndExistingImagePicker}
                                existingAvatarUrl={
                                    organizationId ? urls.api.organizations.logo(organizationId) : undefined
                                }
                                title={intl('organizationLogo')}
                                imagePickerOptions={{
                                    allowsEditing: true,
                                    quality: AVATAR_UPLOAD_QUALITY,
                                }}
                                sizeVariant="logo"
                                addPhotoText={intl('addLogo')}
                            />
                        </MaybeStackedImageField>
                        <MaybeStackedImageField
                            helpTextMain={intl('organizationAvatarMainHelpText')}
                            subHelpText={intl('organizationAvatarSubHelpText')}
                        >
                            <Field
                                name="avatar"
                                component={FormikInputs.TempAndExistingImagePicker}
                                existingAvatarUrl={
                                    organizationId ? urls.api.organizations.avatar(organizationId) : undefined
                                }
                                title={intl('organizationAvatar')}
                                imagePickerOptions={{
                                    allowsEditing: true,
                                    quality: AVATAR_UPLOAD_QUALITY,
                                }}
                                sizeVariant="avatar"
                                addPhotoText={intl('addAvatar')}
                            />
                        </MaybeStackedImageField>
                        {renderColorPicker(
                            'brandingPrimaryColor',
                            intl('brandingPrimaryColorTitle'),
                            intl('brandingPrimaryColorSubTitle'),
                        )}
                        {renderColorPicker(
                            'brandingSecondaryColor',
                            intl('brandingSecondaryColorTitle'),
                            intl('brandingSecondaryColorSubTitle'),
                        )}
                        <CenteredFormRow>
                            <Field
                                name="description"
                                component={FormikInputs.TextArea}
                                title={intl('description')}
                                forceFullWidth
                                style={{ width: '100%' }}
                            />
                        </CenteredFormRow>
                        {renderFields()}
                        <FormDirtyStateTracker />
                        <Stack vertical space={getSpacing(2)} fullWidth>
                            <Field
                                name="isPubliclyListed"
                                component={FormikInputs.Switch}
                                title={intl('isPublic')}
                                // Only allow the switch to be toggled if the user is the owner of the organization
                                // If the organization is being created, we can assume the user is in effect the owner
                                isDisabled={!isOwner && !isNewOrganization}
                            />
                            <Text variant="caption-2" wrapText style={{ maxWidth: '100%' }}>
                                {intl('publicExplainer')}
                            </Text>
                        </Stack>
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(OrganizationDetailsForm);
